import { api } from '../api.js';
import { setCachedPermissions } from '../utils/permissions.js';
import { setToken } from '../utils/tokenUtils.js';

function applyAuthContext(ctx) {
  if (!ctx) {
    return null;
  }
  if (ctx.permissions) {
    setCachedPermissions(ctx.permissions);
  }
  if (ctx.tenantId) {
    localStorage.setItem('tenantId', String(ctx.tenantId).trim());
    const saved = localStorage.getItem('user');
    if (saved) {
      try {
        const user = JSON.parse(saved);
        user.tenantId = ctx.tenantId;
        if (ctx.tenantName) user.tenantName = ctx.tenantName;
        localStorage.setItem('user', JSON.stringify(user));
      } catch (e) {
        console.warn('同步 auth context 到 user 失败', e);
      }
    }
  }
  localStorage.setItem('authContext', JSON.stringify(ctx));
  if (ctx.deployment) {
    localStorage.setItem('deploymentContext', JSON.stringify(ctx.deployment));
  }
  return ctx;
}

function mergeAuthContextStats(partial) {
  if (!partial) {
    return;
  }
  try {
    const raw = localStorage.getItem('authContext');
    if (!raw) {
      return;
    }
    const ctx = JSON.parse(raw);
    if (partial.stats) {
      ctx.stats = partial.stats;
      ctx.statsLoaded = true;
    }
    if (partial.accessibleTenantIds != null) {
      ctx.accessibleTenantIds = partial.accessibleTenantIds;
    }
    localStorage.setItem('authContext', JSON.stringify(ctx));
  } catch (e) {
    console.warn('合并 auth context 统计失败', e);
  }
}

/**
 * 双端统一初始化：JWT 租户 + 权限（默认跳过统计，登录 2–3 秒内返回）
 * @param {{ includeStats?: boolean, backgroundStats?: boolean }} options
 */
export const fetchAuthContext = async ({ includeStats = false, backgroundStats = true } = {}) => {
  const response = await api.get('/auth/context', {
    params: { includeStats },
  });
  const ctx = applyAuthContext(response?.data?.data);
  if (backgroundStats && !includeStats && ctx) {
    fetchAuthContextStats().catch((err) => console.warn('后台拉取 auth 统计失败:', err));
  }
  return ctx;
};

/** 异步补拉农场/地块等统计摘要，合并进本地 authContext 缓存 */
export const fetchAuthContextStats = async () => {
  const response = await api.get('/auth/context', {
    params: { includeStats: true },
    timeout: 120000,
  });
  const partial = response?.data?.data;
  mergeAuthContextStats(partial);
  return partial?.stats;
};

export const getCachedAuthContext = () => {
  try {
    const raw = localStorage.getItem('authContext');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

/** 审批通过或租户变更后刷新 JWT，并同步 authContext */
export const refreshAuthSession = async () => {
  const response = await api.post('/auth/refresh-session', {});
  const payload = response?.data ?? {};
  const token = payload.token || payload.accessToken;
  if (token) {
    setToken(token);
  }
  if (payload.user) {
    localStorage.setItem('user', JSON.stringify(payload.user));
    if (payload.user.tenantId) {
      localStorage.setItem('tenantId', String(payload.user.tenantId).trim());
    }
  }
  if (payload.permissions) {
    setCachedPermissions(payload.permissions);
  }
  await fetchAuthContext({ includeStats: false, backgroundStats: false });
  return payload;
};
